import { useCallback, useEffect, useRef, useState } from 'react';
import liveAPI from '../../../services/liveApi';
import type { UseMicCaptureResult } from './useMicCapture';

// Minimal type for the transcriber instance we hold (the SDK is dynamically
// imported so we can't reference its classes at module scope). We use
// ConversationTranscriber (not SpeechRecognizer) so each finalized utterance
// carries a speakerId for real-time diarization (speaker separation).
interface TranscriberLike {
  transcribing: ((sender: unknown, e: { result: { text?: string } }) => void) | undefined;
  transcribed:
    | ((sender: unknown, e: { result: { reason: unknown; text?: string; speakerId?: string } }) => void)
    | undefined;
  canceled: ((sender: unknown, e: { errorDetails?: string }) => void) | undefined;
  startTranscribingAsync: (cb?: () => void, err?: (e: unknown) => void) => void;
  stopTranscribingAsync: (cb?: () => void, err?: (e: unknown) => void) => void;
  close: () => void;
}

// Azure diarization returns opaque speaker ids like "Guest-1"/"Guest-2" (or
// "Unknown" before it has enough audio to separate a voice). Present them as
// friendly "Speaker 1" labels; leave truly-unknown speakers unlabelled so the
// caller posts them anonymously rather than mislabelling.
const friendlySpeaker = (speakerId?: string): string | undefined => {
  if (!speakerId) return undefined;
  const id = speakerId.trim();
  if (!id || /^unknown$/i.test(id)) return undefined;
  // Normalise the diarization id shapes to "Speaker N": "Guest-1", "Guest 1",
  // "Speaker 1", or a bare "1" all become "Speaker 1".
  const m = /^(?:guest|speaker)?[\s-]*(\d+)$/i.exec(id);
  if (m) return `Speaker ${m[1]}`;
  return id;
};

// Azure AI Speech real-time microphone transcription. Presents the same shape
// as useMicCapture (supported/listening/error/interim/start/stop) so LiveMeeting
// can swap between this and the browser Web Speech fallback transparently.
//
// The browser holds only a short-lived authorization token (minted per session
// by the API); the subscription key never reaches the client. `transcribing`
// events stream interim hypotheses for instant feedback; `transcribed` events
// carry finalized utterances (with a diarized speakerId) that become transcript
// segments.
export const useAzureSpeech = (
  meetingId: string | undefined,
  onFinalResult: (text: string, speaker?: string) => void,
  // Invoked when the Speech feature is not configured for this environment (the
  // token endpoint answers 503). The orchestrator uses this to fall back to the
  // browser Web Speech API without a second token mint.
  onUnavailable?: () => void
): UseMicCaptureResult => {
  const recognizerRef = useRef<TranscriberLike | null>(null);
  const callbackRef = useRef<(text: string, speaker?: string) => void>(onFinalResult);
  const unavailableRef = useRef(onUnavailable);
  const wantListeningRef = useRef(false);
  const startingRef = useRef(false);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [interim, setInterim] = useState('');

  useEffect(() => {
    callbackRef.current = onFinalResult;
  }, [onFinalResult]);

  useEffect(() => {
    unavailableRef.current = onUnavailable;
  }, [onUnavailable]);

  const teardown = useCallback(() => {
    const recognizer = recognizerRef.current;
    recognizerRef.current = null;
    if (recognizer) {
      try {
        recognizer.stopTranscribingAsync(
          () => {
            try {
              recognizer.close();
            } catch {
              // ignore
            }
          },
          () => {
            try {
              recognizer.close();
            } catch {
              // ignore
            }
          }
        );
      } catch {
        // ignore
      }
    }
    setListening(false);
    setInterim('');
  }, []);

  const start = useCallback(() => {
    if (!meetingId || recognizerRef.current || startingRef.current) return;
    startingRef.current = true;
    wantListeningRef.current = true;
    setError(null);

    (async () => {
      // Token first: a 409 (not controller) or 503 (Speech unconfigured) fails
      // fast before the SDK bytes are downloaded, so the caller can fall back.
      const tokenResponse = await liveAPI.getSpeechToken(meetingId);

      const sdk = await import('microsoft-cognitiveservices-speech-sdk');

      const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(
        tokenResponse.token,
        tokenResponse.region
      );
      speechConfig.speechRecognitionLanguage = tokenResponse.language;

      // Proactively refresh the ~10-minute STS token so long sessions don't drop.
      const refreshTimer = setInterval(() => {
        liveAPI
          .getSpeechToken(meetingId)
          .then((r) => {
            speechConfig.authorizationToken = r.token;
          })
          .catch(() => {
            /* transient; the next tick retries */
          });
      }, 8 * 60 * 1000);

      const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();
      const recognizer = new sdk.ConversationTranscriber(speechConfig, audioConfig) as unknown as TranscriberLike;

      recognizer.transcribing = (_s, e) => {
        setInterim((e.result.text || '').trim());
      };
      recognizer.transcribed = (_s, e) => {
        setInterim('');
        if (e.result.reason === sdk.ResultReason.RecognizedSpeech) {
          const text = (e.result.text || '').trim();
          if (text) callbackRef.current(text, friendlySpeaker(e.result.speakerId));
        }
      };
      recognizer.canceled = (_s, e) => {
        clearInterval(refreshTimer);
        if (wantListeningRef.current) {
          setError(e.errorDetails || 'Speech recognition canceled');
        }
        teardown();
      };

      if (!wantListeningRef.current) {
        // stop() was called during startup; abandon this recognizer.
        clearInterval(refreshTimer);
        try {
          recognizer.close();
        } catch {
          // ignore
        }
        return;
      }

      recognizerRef.current = recognizer;
      recognizer.startTranscribingAsync(
        () => setListening(true),
        (err) => {
          clearInterval(refreshTimer);
          setError(err instanceof Error ? err.message : 'Failed to start microphone');
          teardown();
        }
      );
    })()
      .catch((err) => {
        wantListeningRef.current = false;
        setListening(false);
        // 503 => feature off for this environment: hand off to the fallback
        // instead of surfacing an error the user can't act on.
        if ((err as { status?: number }).status === 503 && unavailableRef.current) {
          unavailableRef.current();
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to start Speech recognition');
      })
      .finally(() => {
        startingRef.current = false;
      });
  }, [meetingId, teardown]);

  const stop = useCallback(() => {
    wantListeningRef.current = false;
    teardown();
  }, [teardown]);

  useEffect(() => {
    return () => {
      wantListeningRef.current = false;
      teardown();
    };
  }, [teardown]);

  // Supported wherever getUserMedia exists (the SDK captures via WebRTC).
  const supported =
    typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

  return { supported, listening, error, interim, start, stop };
};
